import React, { useState, useEffect } from "react";
import { Card, Form, Input, Button, Row, Col, App, Typography, Space, Alert, Progress, Divider, Avatar,  theme, Result } from "antd";
import { LockOutlined, ArrowLeftOutlined, UserOutlined } from "@ant-design/icons";
import api from "../services/api";
import { useNavigate, useSearchParams } from "react-router";
import { useLanguage } from "../providers/LanguageProvider";

const { Title, Text } = Typography;

function calcStrength(pw: string) {
  let score = 0;
  if (!pw) return { score: 0, percent: 0, level: 'weak' };
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw)) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;

  const percent = Math.min(100, Math.round((score / 6) * 100));
  let level = 'weak';
  if (percent >= 70 && pw.length >= 10) level = 'medium';
  if (percent >= 85 && pw.length >= 12) level = 'strong';
  return { score, percent, level };
}

export default function ResetPassword() {
  const { token } = theme.useToken();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");

  const [form] = Form.useForm();
  const [capsLock, setCapsLock] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const newPw = Form.useWatch('NewPassword', form);
  const { percent, level } = calcStrength(newPw || '');

  const message = App.useApp().message;

  const levelColor =
    level === 'strong' ? token.colorSuccess :
      level === 'medium' ? token.colorWarning :
        token.colorError;

  const onKeyEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
    try {
      setCapsLock(e.getModifierState?.('CapsLock') ?? false);
    } catch {/* noop */ }
  };

  useEffect(() => {
    if (!resetToken) {
      setError(t('RESET_INVALID_LINK'));
      setVerifying(false);
      return;
    }

    setTokenValid(true);
    setVerifying(false);
  }, [resetToken]);

  const handleFinish = async (values) => {
    if (values.NewPassword !== values.ConfirmPassword) {
      message.warning(t('ERROR_PASSWORD_MISMATCH'));
      return;
    }
    try {
      setLoading(true);
      await api.post("/auth/reset-password?token=" + resetToken, {
        newPassword: values.NewPassword,
      });
      message.success(t('PASSWORD_RESET_SUCCESS'));
      navigate("/login");
    } catch (err) {

      const errCode = err?.response?.data?.message
      message.error(t(errCode))
      setApiError(errCode);
    } finally {
      setLoading(false);
    }
  };

  const rules = {
    requiredNew: [
      { required: true, message: t('ERROR_NEW_PASSWORD_REQUIRED') },
      { min: 8, message: t('ERROR_MIN8') },
      {
        validator: async (_, value) => {
          if (!value) return;
          const fails = [];
          if (!/[a-z]/.test(value)) fails.push(t('RULE_LOWER'));
          if (!/[A-Z]/.test(value)) fails.push(t('RULE_UPPER'));
          if (!/\d/.test(value)) fails.push(t('RULE_DIGIT'));
          if (!/[^A-Za-z0-9]/.test(value)) fails.push(t('RULE_SPECIAL'));
          if (fails.length) return Promise.reject(new Error(fails.join(', ')));
          return Promise.resolve();
        },
      },
    ],
    confirm: [
      { required: true, message: t('ERROR_CONFIRM_PASSWORD_REQUIRED') },
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (!value || getFieldValue('NewPassword') === value) return Promise.resolve();
          return Promise.reject(new Error(t('ERROR_PASSWORDS_NOT_MATCH')));
        },
      }),
    ],
  };

  if (verifying) {
    return (
      <div style={{ minHeight: "100svh", display: "grid", placeItems: "center", padding: 24 }}>
        <Card style={{ width: "100%", maxWidth: 480, borderRadius: 16, boxShadow: "0 8px 30px rgba(0,0,0,0.06)" }}>
          <div style={{ textAlign: "center" }}>
            <Title level={3}>{t('RESET_VERIFYING')}</Title>
            <Text type="secondary">{t('RESET_CHECKING_LINK')}</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !tokenValid) {
    return (
      <div style={{ minHeight: "100svh", display: "grid", placeItems: "center", padding: 24 }}>
        <Card style={{ width: "100%", maxWidth: 480, borderRadius: 16, boxShadow: "0 8px 30px rgba(0,0,0,0.06)" }}>
          <Result
            status="error"
            title={t('RESET_LINK_INVALID')}
            subTitle={error || t('RESET_LINK_EXPIRED')}
            extra={
              <Button type="primary" onClick={() => navigate("/forgot-password")}>
                {t('BTN_REQUEST_NEW_RESET')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100svh", display: "grid", placeItems: "center", padding: 24 }}>
      <Card style={{ width: "100%", maxWidth: 480, borderRadius: 16, boxShadow: "0 8px 30px rgba(0,0,0,0.06)" }}>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <Avatar size={72} icon={<UserOutlined />} style={{ background: token.colorPrimary, marginBottom: 8 }} />
        </div>

        <Title level={3} style={{ textAlign: "center", marginBottom: 6, fontWeight: 700 }}>
          {t('RESET_SET_NEW_PASSWORD')}
        </Title>
        <Text type="secondary" style={{ display: "block", textAlign: "center" }}>
          {t('RESET_SET_PASSWORD_DESC')}
        </Text>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          requiredMark="optional"
          style={{ marginTop: 20 }}
        >
          {apiError && (
            <Alert
            banner
              type="error"
              showIcon
              message={t('ERROR_GENERIC')}
              description={t(apiError)}
              style={{ marginBottom: 16 }}
            />
          )}

          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                label={t('LABEL_NEW_PASSWORD')}
                name="NewPassword"
                rules={rules.requiredNew}
                hasFeedback
                validateTrigger={['onChange', 'onBlur']}
                extra={
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">{t('PASSWORD_STRENGTH')}:</Text>{' '}
                      <Text strong style={{ color: levelColor }}>
                        {level === 'strong' ? t('STRONG') : level === 'medium' ? t('MEDIUM') : t('WEAK')}
                      </Text>
                    </div>
                    <Progress
                      percent={percent}
                      showInfo={false}
                      strokeColor={levelColor}
                      status={percent === 100 ? 'normal' : 'active'}
                    />
                    <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                      <li>{t('RULE_MIN8')}</li>
                      <li>{t('RULE_COMPLEXITY')}</li>
                    </ul>
                  </Space>
                }
              >
                <Input.Password
                  placeholder={t('PLACEHOLDER_NEW_PASSWORD')}
                  autoComplete="new-password"
                  onKeyUp={onKeyEvent}
                />
              </Form.Item>

              <Form.Item
                label={t('LABEL_CONFIRM_PASSWORD')}
                name="ConfirmPassword"
                dependencies={['NewPassword']}
                rules={rules.confirm}
                hasFeedback
                validateTrigger={['onChange', 'onBlur']}
              >
                <Input.Password
                  placeholder={t('PLACEHOLDER_CONFIRM_PASSWORD')}
                  autoComplete="new-password"
                  onKeyUp={onKeyEvent}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item shouldUpdate>
            {() => {
              const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
              const { NewPassword, ConfirmPassword } = form.getFieldsValue([
                'NewPassword', 'ConfirmPassword'
              ]);
              const allFilled = !!NewPassword && !!ConfirmPassword;

              return (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <Button onClick={() => form.resetFields()}>{t('BTN_RESET')}</Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={!allFilled || hasErrors}
                  >
                    {t('BTN_UPDATE_PASSWORD')}
                  </Button>
                </div>
              );
            }}
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
